import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
// biome-ignore lint/performance/noNamespaceImport: intentional
import * as Sentry from '@sentry/tanstackstart-react';
import { QueryClientProvider } from '@tanstack/react-query';
import { createRouter } from '@tanstack/react-router';
import { createStart } from '@tanstack/react-start';

import { routeTree } from '@app/routeTree.gen';
import store from '@app/store';
import { queryClient } from '@shared/api/query-client';

// TODO: remove this shim once react-data-grid@6 is gone.
// react-data-grid@6.1.0 is EOL (peer react ^16) and its bundle calls ReactDOM.findDOMNode,
// which was removed in React 19. The shim emulates findDOMNode via React's private
// `_reactInternals` fiber field — fragile and tied to React internals.
// biome-ignore lint/suspicious/noExplicitAny: intentional
const _findDOMNodeShim = (instance: any): Element | null => {
  if (instance == null) {
    return null;
  }

  if (instance instanceof Element) {
    return instance;
  }

  const fiber = instance._reactInternals;

  if (!fiber) {
    return null;
  }

  let node = fiber;
  while (node) {
    if (node.stateNode instanceof Element) {
      return node.stateNode;
    }

    node = node.child;
  }
  return null;
};
// biome-ignore lint/suspicious/noExplicitAny: intentional
if (!(ReactDOM as any).findDOMNode) {
  // biome-ignore lint/suspicious/noExplicitAny: intentional
  (ReactDOM as any).findDOMNode = _findDOMNodeShim;
}
// biome-ignore lint/suspicious/noExplicitAny: intentional
if (!(ReactDOM as any).default) {
  // biome-ignore lint/suspicious/noExplicitAny: intentional
  (ReactDOM as any).default = ReactDOM;
}

createStart(() => ({
  defaultSsr: false,
  requestMiddleware: [Sentry.sentryGlobalRequestMiddleware],
  functionMiddleware: [Sentry.sentryGlobalFunctionMiddleware],
}));

export const getRouter = () => {
  const router = createRouter({
    routeTree,
    notFoundMode: 'root',
    context: { queryClient },
    scrollRestoration: true,
    // Theme providers moved into the document body (`__root.tsx` shell); a
    // theme provider rendered here, outside `<html>`, makes MantineProvider's
    // CssVariables `<style>` a sibling of `<html>` which React 19 rejects.
    Wrap: ({ children }) => (
      <Provider store={store}>
        <QueryClientProvider client={queryClient}>
          {children}
        </QueryClientProvider>
      </Provider>
    ),
  });

  if (import.meta.env.PROD && !router.isServer) {
    Sentry.init({
      dsn: import.meta.env.SENTRY_DSN,
      environment: import.meta.env.MODE,
      tracesSampleRate: 0.1,
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
      denyUrls: ['https://track.gogocpa.app/', '\\bgogocpa\\b'],
      sendDefaultPii: true,
      enableLogs: true,
      integrations: [
        Sentry.tanstackRouterBrowserTracingIntegration(router),
        Sentry.replayIntegration(),
        Sentry.consoleLoggingIntegration({ levels: ['error'] }),
      ],
    });
  }

  return router;
};

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof getRouter>;
  }
}
